import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader, AlertTriangle, ArrowRight, BookOpenCheck, ShieldCheck, Sparkles, MessageSquareQuote } from "lucide-react";
import { api } from "../lib/api";

const ORANGE = "#FF6A00";
const NAVY = "#0F1B2D";

/**
 * PublicPlaybook — read-only public view of an Interview Playbook, accessed
 * via /playbook/share/:token. Renders the questions list + framework_answer
 * blocks but no edit / delete / framework-rerun controls.
 *
 * Backend: GET /api/public/playbooks/{token}
 */
export default function PublicPlaybook() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/public/playbooks/${token}`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "This link is invalid or sharing was revoked.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen rm-paper flex items-center justify-center">
        <Loader size={28} className="animate-spin rm-text-orange" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen rm-paper flex items-center justify-center p-6">
        <div className="rm-card-static p-6 max-w-md text-center"
             style={{ background: "#FEE2E2", borderColor: "#B91C1C" }}
             data-testid="public-playbook-error">
          <AlertTriangle size={32} style={{ color: "#B91C1C", margin: "0 auto" }} />
          <h2 className="font-display uppercase text-xl mt-3"
              style={{ color: NAVY, letterSpacing: "-0.02em" }}>Link unavailable</h2>
          <p className="text-sm mt-1" style={{ color: "#991B1B" }}>{error || "Try requesting a fresh link from the sharer."}</p>
          <Link to="/" className="rm-btn rm-btn-primary mt-4 text-sm px-3 py-2 inline-flex">
            Go to Resmo <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    );
  }

  const pb = data.playbook || {};
  const questions = pb.questions || [];

  return (
    <div className="min-h-screen rm-paper" data-testid="public-playbook">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-10">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <Link to="/" className="font-display uppercase text-2xl"
                style={{ color: NAVY, letterSpacing: "-0.02em" }}>
            Resmo
          </Link>
          <span className="font-mono-rm uppercase text-[10px] tracking-widest inline-flex items-center gap-1 px-2 py-1 rounded"
                style={{ background: "#D1FAE5", color: "#064E3B", border: "1px solid #047857", fontWeight: 700 }}
                data-testid="public-playbook-readonly-pill">
            <ShieldCheck size={11} /> Read-only · shared by {data.shared_by}
          </span>
        </div>

        <div className="rm-card-static p-5 md:p-6"
             style={{ background: `linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)`, borderColor: NAVY, boxShadow: `4px 4px 0 0 #1E40AF` }}
             data-testid="public-playbook-hero">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1 inline-flex items-center gap-1"
               style={{ color: "#1E40AF", fontWeight: 700 }}>
            <BookOpenCheck size={11} /> · Resmo Interview Playbook
          </div>
          <h1 className="font-display uppercase leading-tight"
              style={{ fontSize: "clamp(1.4rem, 3vw, 2.2rem)", color: NAVY, letterSpacing: "-0.02em" }}>
            {data.job_title || "Interview prep"}{data.company_name ? <span style={{ color: "#475569" }}>  ·  {data.company_name}</span> : null}
          </h1>
          <p className="text-sm mt-1" style={{ color: "#475569" }}>
            {questions.length} curated question{questions.length === 1 ? "" : "s"} — coach the candidate by reviewing each answer block below.
          </p>
        </div>

        <ul className="space-y-3 mt-4" data-testid="public-playbook-questions">
          {questions.map((q, idx) => (
            <li key={q.id || idx}
                className="rm-card-static p-4"
                style={{ background: "white", borderColor: "#E2E8F0" }}
                data-testid={`public-q-${idx}`}>
              <div className="flex items-start gap-2 mb-2">
                <span className="font-display text-lg"
                      style={{ color: ORANGE, width: 28, flexShrink: 0 }}>
                  {idx + 1}.
                </span>
                <div className="flex-1">
                  <div className="text-base font-bold leading-snug" style={{ color: NAVY }}>
                    {q.question}
                  </div>
                  {q.source && (
                    <span className="font-mono-rm uppercase text-[9px] tracking-widest mt-1 inline-block"
                          style={{ color: "#94A3B8", fontWeight: 700 }}>
                      · {q.source}
                    </span>
                  )}
                </div>
              </div>
              {Array.isArray(q.bullets) && q.bullets.length > 0 && (
                <ul className="ml-7 space-y-1.5 mt-2">
                  {q.bullets.map((b, bi) => (
                    <li key={bi} className="flex items-start gap-2 text-[14px]" style={{ color: NAVY }}>
                      <span className="rm-text-orange flex-shrink-0">·</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {q.framework_answer?.bullets && (
                <div className="rm-card-static p-3 mt-3 ml-7"
                     style={{ background: "#FFF7F2", borderColor: ORANGE }}>
                  <div className="font-mono-rm uppercase text-[9px] tracking-widest mb-1 inline-flex items-center gap-1"
                       style={{ color: ORANGE, fontWeight: 700 }}>
                    <MessageSquareQuote size={10} /> · Framework: {q.framework_answer.framework || "STAR"}
                  </div>
                  {q.framework_answer.bullets.map((line, fi) => (
                    <p key={fi} className="text-[13px] leading-relaxed mt-1" style={{ color: NAVY }}>
                      <span className="font-mono-rm uppercase text-[10px] tracking-widest mr-2"
                            style={{ color: ORANGE, fontWeight: 700 }}>
                        {line.label || "·"}
                      </span>
                      {line.text}
                    </p>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>

        <div className="rm-card-static p-5 mt-6 text-center"
             style={{ background: NAVY, borderColor: NAVY, boxShadow: `4px 4px 0 0 ${ORANGE}` }}>
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
               style={{ color: ORANGE, fontWeight: 700 }}>
            · Built with Resmo
          </div>
          <p className="text-sm mb-3" style={{ color: "#CBD5E1" }}>
            Resmo builds interview playbooks from any job description in under a minute.
          </p>
          <Link to="/" className="rm-btn text-sm px-4 py-2"
                style={{ background: ORANGE, color: "white", border: "2px solid white", fontWeight: 700 }}>
            Try Resmo free <Sparkles size={14} />
          </Link>
        </div>

        <footer className="text-center mt-6">
          <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
            · {new Date(data.created_at).toLocaleDateString()} · Playbook shared via Resmo
          </span>
        </footer>
      </div>
    </div>
  );
}
